import React, { useState } from 'react';
import { X, DollarSign } from 'lucide-react';

const IfadaPaymentModal = ({ order, onClose, onPaymentSuccess, apiCall, selectedYear, showError, showSuccess }) => {
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [loading, setLoading] = useState(false);

  if (!order) return null;

  const formatLBP = (amount) => {
    return new Intl.NumberFormat('en-US').format(amount) + ' ل.ل';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      const paymentData = {
        familyId: order.FamilyID,
        paymentMethod: paymentMethod,
        academicYear: selectedYear
      };
      
      // ✅ FIXED: Use correct API call format
      const result = await apiCall(`/ifada/orders/${order.OrderID}/pay`, {
        method: 'POST',
        body: JSON.stringify(paymentData)
      });
      
      showSuccess('Payment recorded successfully');
      onPaymentSuccess(result.paymentId);
      onClose();
    } catch (error) {
      showError('Error recording payment: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-800">Record Ifada Payment</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Order Info */}
          <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <p className="text-sm text-gray-600">Order #</p>
                <p className="font-semibold">#{order.OrderID}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Family</p>
                <p className="font-semibold">{order.FamilyID}</p>
              </div>
            </div>
            
            <div className="mb-3">
              <p className="text-sm text-gray-600">Student</p>
              <p className="font-semibold">{order.StudentName}</p>
              {order.StudentClass && (
                <p className="text-sm text-gray-500">Class: {order.StudentClass}</p>
              )}
            </div>

            {order.ItemCount && (
              <div className="mb-3">
                <p className="text-sm text-gray-600">Items</p>
                <p className="font-semibold">{order.ItemCount} item(s)</p>
              </div>
            )}

            <div className="pt-3 border-t border-purple-300">
              <p className="text-sm text-gray-600 mb-1">Total Amount</p>
              <p className="text-2xl font-bold text-purple-600">{formatLBP(order.TotalAmount)}</p>
            </div>
          </div>

          {/* Payment Method */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Method *
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            >
              <option value="Cash">Cash / نقداً</option>
              <option value="Check">Check / شيك</option>
              <option value="Bank Transfer">Bank Transfer / تحويل بنكي</option>
              <option value="Credit Card">Credit Card / بطاقة ائتمان</option>
            </select>
          </div>

          {/* Info Note */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              💡 This payment will be recorded in Lebanese Pounds (LBP) and will appear in the Ifada Payments tab.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <DollarSign size={18} />
              {loading ? 'Processing...' : 'Confirm Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IfadaPaymentModal;